import React, { useState } from "react";
import Modal from "./Modal";
import ConfirmationModal from "./ConfirmationModal";
import { KeywordWithMetadata } from "../types";
import { clusterApi, KeywordReplacementUsage } from "../services/api";
import { useClient } from "../contexts/ClientContext";
import { useAuth } from "../contexts/AuthContext";
import "./DeleteKeywordsModal.css";
import { LOW_REPLACEMENT_QUOTA_THRESHOLD } from "../config/constants";

interface DeleteKeywordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  keywords: KeywordWithMetadata[];
  anchorKeyword: string;
  onDeleteSuccess: () => void;
  locationCode?: number;
  languageCode?: string;
  replacementUsage?: KeywordReplacementUsage | null;
}

const DeleteKeywordsModal: React.FC<DeleteKeywordsModalProps> = ({
  isOpen,
  onClose,
  keywords,
  anchorKeyword,
  onDeleteSuccess,
  locationCode = 2840,
  languageCode = "en",
  replacementUsage,
}) => {
  const { currentClient } = useClient();
  const { user } = useAuth();
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(
    new Set()
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string>("");
  const [isQuotaWarningOpen, setIsQuotaWarningOpen] = useState(false);
  const canDelete = !!user && (user.is_superadmin || user.role === "admin");

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setSelectedKeywords(new Set());
      setError("");
      setIsQuotaWarningOpen(false);
    }
  }, [isOpen]);

  const handleKeywordToggle = (keyword: string) => {
    if (!canDelete) {
      return;
    }
    const newSelected = new Set(selectedKeywords);
    if (newSelected.has(keyword)) {
      newSelected.delete(keyword);
    } else {
      newSelected.add(keyword);
    }
    setSelectedKeywords(newSelected);
  };

  const handleSelectAll = () => {
    if (!canDelete) {
      return;
    }
    // Filter out anchor keywords from selectable keywords
    const selectableKeywords = keywords.filter((k) => !k.isAnchor);

    if (selectedKeywords.size === selectableKeywords.length) {
      setSelectedKeywords(new Set());
    } else {
      setSelectedKeywords(new Set(selectableKeywords.map((k) => k.keyword)));
    }
  };

  const executeDelete = async () => {
    setIsDeleting(true);
    setError("");

    try {
      const result = await clusterApi.deleteKeywords(currentClient, {
        anchor_keyword: anchorKeyword,
        keywords: Array.from(selectedKeywords),
        location_code: locationCode,
        language_code: languageCode,
      });

      console.log("Delete successful:", result);
      onDeleteSuccess();
      onClose();
    } catch (error) {
      console.error("Failed to delete keywords:", error);
      setError(
        error instanceof Error ? error.message : "Failed to delete keywords"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const quotaRemaining =
    replacementUsage?.limit != null
      ? Math.max(0, replacementUsage.limit - replacementUsage.used)
      : null;

  const handleDelete = async () => {
    if (!canDelete) {
      setError("Only tenant admins can delete keywords.");
      return;
    }
    if (selectedKeywords.size === 0) {
      setError("Please select at least one keyword to delete.");
      return;
    }

    if (
      quotaRemaining !== null 
      && (
        quotaRemaining < selectedKeywords.size 
        || quotaRemaining <= LOW_REPLACEMENT_QUOTA_THRESHOLD
      )
    ) {
      setIsQuotaWarningOpen(true);
      return;
    }

    await executeDelete();
  };

  if (!isOpen) return null;

  return (
    <>
      <ConfirmationModal
        isOpen={isQuotaWarningOpen}
        onClose={() => setIsQuotaWarningOpen(false)}
        onConfirm={executeDelete}
        title="Replacement Quota Warning"
        message={`You are about to remove ${selectedKeywords.size} keyword(s) from this cluster. Your remaining keyword replacement quota for this cluster is ${quotaRemaining}. Once your quota is exhausted, you will not be able to add new keywords until the next billing period. Do you want to continue?`}
        confirmText="Remove Keywords"
        cancelText="Cancel"
        variant="danger"
      />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Remove Keywords"
        maxWidth="800px"
      >
        <div className="delete-keywords-modal">
          {error && <div className="error-message">{error}</div>}
          {!canDelete && (
            <div className="error-message">
              Only tenant admins can delete keywords.
            </div>
          )}

          <div className="keywords-controls">
            <button
              type="button"
              className="select-all-btn"
              onClick={handleSelectAll}
              disabled={!canDelete}
            >
              {selectedKeywords.size ===
              keywords.filter((k) => !k.isAnchor).length
                ? "Deselect All"
                : "Select All"}
            </button>
            <span className="selection-count">
              {selectedKeywords.size} of{" "}
              {keywords.filter((k) => !k.isAnchor).length} selected
            </span>
          </div>

          <div className="delete-keywords-list">
            <div className="keywords-header">
              <div></div>
              <div className="keyword-col">Keyword</div>
              <div className="volume-col">Volume</div>
              <div className="difficulty-col">Difficulty</div>
              <div className="ranking-col">Ranking</div>
              <div className="relevance-col">Relevance</div>
            </div>

            <div className="keywords-body">
              {keywords.map((keyword) => (
                <div
                  key={keyword.keyword}
                  className={`keyword-row ${
                    selectedKeywords.has(keyword.keyword) ? "selected" : ""
                  } ${keyword.isAnchor ? "anchor-keyword" : ""}`}
                  onClick={() => {
                    if (!keyword.isAnchor && canDelete) {
                      handleKeywordToggle(keyword.keyword);
                    }
                  }}
                  style={{ cursor: keyword.isAnchor || !canDelete ? 'not-allowed' : 'pointer' }}
                >
                  <div 
                    className="keyword-checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <label htmlFor={`keyword-${keyword.keyword}`}>
                      <input
                        type="checkbox"
                        id={`keyword-${keyword.keyword}`}
                        checked={selectedKeywords.has(keyword.keyword)}
                        onChange={() => handleKeywordToggle(keyword.keyword)}
                        disabled={keyword.isAnchor || !canDelete}
                      />
                      <span className="keyword-checkbox-custom"></span>
                    </label>
                  </div>
                  <div className="keyword-col">
                    <span>
                      {keyword.keyword}
                      {keyword.isAnchor && (
                        <span className="anchor-badge">Anchor</span>
                      )}
                    </span>
                  </div>
                  <div className="volume-col">
                    {keyword.volume?.toLocaleString() || "N/A"}
                  </div>
                  <div className="difficulty-col">
                    {keyword.difficulty || "N/A"}
                  </div>
                  <div className="ranking-col">{keyword.ranking || "N/A"}</div>
                  <div className="relevance-col">
                    {keyword.openai_similarity || keyword.relevance_score
                      ? (
                          (keyword.openai_similarity ||
                            (keyword.relevance_score as number)) * 100
                        ).toFixed(1) + "%"
                      : "N/A"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="delete-btn"
              onClick={handleDelete}
              disabled={!canDelete || isDeleting || selectedKeywords.size === 0}
            >
              {isDeleting
                ? "Deleting..."
                : `Delete ${selectedKeywords.size} Keywords`}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DeleteKeywordsModal;
